import type {Textbox} from '@postermywall/fabricjs-2';
import type {TextStyles} from '@PosterWhiteboard/classes/text-styles.class';
import type {TextRunStylesObject, TextSelectionStylesSummary} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import {applyRunStylesToTextboxSelection} from '@PosterWhiteboard/items/text-item/text-style-runs-compiler';

interface PendingTypingStylesEntry {
  caretIndex: number;
  styles: Partial<TextRunStylesObject>;
}

/**
 * Rich-text "pending typing styles": a style change made at a COLLAPSED caret while editing does
 * not restyle anything yet — it buffers here and applies to the next characters typed at that
 * caret, after which fabric's normal typing inheritance carries the style forward. Moving the
 * caret, making a selection, or ending the edit session discards the buffer, matching standard
 * editors. One instance lives on each text-editing owner (TextItem and SlideshowItem).
 *
 * Consume ordering: fabric processes the hidden textarea's `input` event first (inserting the
 * characters and firing `selection:changed` synchronously), and the owner's own `input` listener
 * runs after it. {@link armOnBeforeInput} therefore flags the insertion from `beforeinput` so the
 * mid-insertion selection change does not discard the buffer before {@link consumeOnInput} sees it.
 */
export class PendingTypingStyles {
  #pendingEntry: PendingTypingStylesEntry | null = null;
  /** Length of the armed insertion's text — bounds what {@link consumeOnInput} may style. */
  #armedInsertText: string | null = null;

  /** Buffers (or extends the buffer of) styles for the caret the textbox currently shows. */
  public mergeAtCaret(editingTextbox: Textbox, stylesPartial: Partial<TextRunStylesObject>): void {
    if (editingTextbox.selectionStart !== editingTextbox.selectionEnd) {
      return;
    }
    const caretIndex = editingTextbox.selectionStart;
    const bufferedStyles = this.#pendingEntry?.caretIndex === caretIndex ? this.#pendingEntry.styles : {};
    this.#pendingEntry = {caretIndex, styles: {...bufferedStyles, ...stylesPartial}};
  }

  /**
   * Overlays the buffered styles onto the caret's published summary, so the style surfaces
   * display what typing is about to produce (a toggled-on Bold button before any bold character
   * exists). The buffer is only ever held for the current collapsed caret, so no index check is
   * needed beyond the summary's own collapsed flag.
   */
  public overlayOntoSummary(summary: TextSelectionStylesSummary): TextSelectionStylesSummary {
    if (this.#pendingEntry === null || !summary.isSelectionCollapsed) {
      return summary;
    }
    return {...summary, ...this.#pendingEntry.styles};
  }

  /**
   * Call from the hidden textarea's `beforeinput` listener. Arms only insertions whose text is
   * known (`event.data`): the consume can then verify the inserted range matches, so an armed
   * flag left over by an insertion that never produced an `input` event (a cancelled IME
   * composition) can never style a stale range.
   */
  public armOnBeforeInput(event: InputEvent): void {
    if (this.#pendingEntry !== null && event.inputType.startsWith('insert') && event.data !== null && event.data.length > 0) {
      this.#armedInsertText = event.data;
    }
  }

  /**
   * Call from the hidden textarea's `input` listener, after fabric has inserted the characters:
   * applies the buffer to the inserted range and discards it. Returns whether styles were applied
   * (the caller re-renders and republishes on true).
   */
  public consumeOnInput(editingTextbox: Textbox, baseStyles: TextStyles): boolean {
    const armedInsertText = this.#armedInsertText;
    this.#armedInsertText = null;
    if (armedInsertText === null || this.#pendingEntry === null) {
      return false;
    }
    const {caretIndex, styles} = this.#pendingEntry;
    this.#pendingEntry = null;
    const insertionEnd = editingTextbox.selectionStart;
    // fabric's caret is a GRAPHEME index while event.data.length counts UTF-16 units — an emoji
    // is one grapheme but two units, so the length must be measured in graphemes
    const armedInsertLength = editingTextbox.graphemeSplit(armedInsertText).length;
    if (editingTextbox.selectionStart !== editingTextbox.selectionEnd || insertionEnd !== caretIndex + armedInsertLength) {
      return false;
    }
    applyRunStylesToTextboxSelection(editingTextbox, styles, baseStyles, caretIndex, insertionEnd);
    return true;
  }

  /**
   * Call from the owner's `selection:changed` handler: any caret move that is not part of an
   * in-flight insertion discards the buffer. Returns whether it did (the caller's republish then
   * drops the overlay).
   */
  public discardOnSelectionChange(editingTextbox: Textbox): boolean {
    if (this.#pendingEntry === null || this.#armedInsertText !== null) {
      return false;
    }
    if (editingTextbox.selectionStart === this.#pendingEntry.caretIndex && editingTextbox.selectionStart === editingTextbox.selectionEnd) {
      return false;
    }
    this.#pendingEntry = null;
    return true;
  }

  /** Unconditional discard — edit-session exit and paste (pasted text never takes the buffer). */
  public discard(): void {
    this.#pendingEntry = null;
    this.#armedInsertText = null;
  }
}
